import { useContext, useMemo, useRef, useState } from "react";
import { useStyles } from "./RCACreateEditIssuesModal.styles";
import { Box, Divider, FormHelperText, Grid, InputLabel, TextField, Typography } from "@mui/material";
import { IRCACreateIssuesModal, IRCAIssueFormValues } from "./RCACreateEditIssuesModalInterfaces";
import { Form, Formik, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { addRCAIssueFormValidationSchema } from "validations/schemas";
import MainModal from "components/MainModal";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import DoneIcon from "@mui/icons-material/Done";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { IssueEntity } from "entities/RCA/IssueEntity/IssueEntity";
import { useNavigate } from "react-router-dom";
import { CORPORATE_BACKOFFICE_PATHS, USERTYPE } from "constants/constants";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import { UserContext } from "contexts/UserContext/UserContext";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import moment from "moment";
import RCAMeetingTypesDropdown from "../RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import useDateFormat from "hooks/useDateFormat";

const RCACreateEditIssuesModal = ({
    open,
    onClose,
    onAddIssue,
    onAddIssueRedirectTo,
    isFromActionPlan,
    editIssue,
    onError,
}: IRCACreateIssuesModal) => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const [isSaving, setIsSaving] = useState(false);
    const { mills } = RCALibraryEntity.GetMills();
    const { keySuccessFactors } = IssueEntity.GetKeySuccessFactors();
    const { user_id } = useContext(UserContext);

    const keySuccessFactorOptions = useMemo(() => {
        if (!keySuccessFactors) return [];
        return keySuccessFactors.map((keySuccessFactor) => ({
            id: keySuccessFactor.key_success_factors_id?.toString() || "",
            name: keySuccessFactor.name ? keySuccessFactor.name : "",
        }));
    }, [keySuccessFactors]);

    const millOptions = useMemo(() => {
        if (!mills) return [];
        return mills.map((m) => ({
            id: m.mill_id?.toString() || "",
            name: m.name ? m.name : "",
        }));
    }, [mills]);

    const handleOnClose = () => {
        onClose();
    };
    const { t } = useTranslation();
    const formRef = useRef<FormikProps<IRCAIssueFormValues>>(null);
    const { currentUser, isLoadingCurrentUser } = useContext(CorporateContext);
    const { createIssue } = IssueEntity.InsertIssue();
    const { updateIssueWithKsfMutation } = IssueEntity.UpdateIssueWithKsf();
    const navigate = useNavigate();
    const { getDateFormatByLanguage } = useDateFormat();

    const isAdmin = useMemo(
        () =>
            currentUser?.user_type?.user_type_id === USERTYPE.ADMINISTRATOR ||
            currentUser?.user_type?.user_type_id === USERTYPE.EXECUTIVE,
        [currentUser],
    );

    const initFormValues: IRCAIssueFormValues = useMemo(
        () =>
            editIssue
                ? {
                      description: editIssue.description,
                      shortDescription: editIssue.shortDescription,
                      incidentDate: editIssue?.incidentDate || editIssue.createdAt,
                      key_success_factors_id: editIssue?.keySuccessFactorId,
                      millId: editIssue.millId,
                      meetingTypeId: editIssue?.meetingTypeId || "",
                      isAdmin,
                  }
                : {
                      description: "",
                      shortDescription: "",
                      incidentDate: new Date(),
                      key_success_factors_id: undefined,
                      millId: undefined,
                      meetingTypeId: "",
                      isAdmin,
                  },
        [editIssue, isAdmin],
    );

    const handleSubmit = async (values: IRCAIssueFormValues) => {
        setIsSaving(true);
        const issueValues = {
            title: values.shortDescription,
            description: values.description,
            incident_date: values.incidentDate,
            ...(!editIssue
                ? {
                      due_date: moment(new Date()).add(4, "days").toDate().toISOString(),
                      status_id: 1,
                      priority_id: 1,
                      created_by: user_id,
                      owner_id: currentUser?.user_id,
                      mill_id: values.millId || currentUser?.mill?.mill_id,
                      ...(values?.meetingTypeId && { meeting_type_id: values.meetingTypeId }),
                      ...(values.key_success_factors_id
                          ? {
                                issue_key_success_factor_indicators: {
                                    data: {
                                        key_success_factor_indicator_id: values.key_success_factors_id,
                                    },
                                },
                            }
                          : {}),
                  }
                : {}),
        };

        try {
            const response = editIssue
                ? await updateIssueWithKsfMutation({
                      variables: {
                          issueId: editIssue.issueId,
                          changes: issueValues,
                          updateIndicator: Boolean(
                              values.key_success_factors_id && initFormValues.key_success_factors_id,
                          ),
                          insertIndicator: Boolean(
                              !initFormValues.key_success_factors_id && values.key_success_factors_id,
                          ),
                          indicatorInsertChanges: [
                              {
                                  key_success_factor_indicator_id: values.key_success_factors_id,
                                  issue_id: editIssue.issueId,
                              },
                          ],
                          indicatorUpdateChanges: {
                              key_success_factor_indicator_id: values.key_success_factors_id,
                          },
                      },
                  })
                : await createIssue({ variables: { issue: issueValues } });
            await onAddIssue();
            setIsSaving(false);
            if (response) {
                if (!editIssue) {
                    const issueId = response.data?.insert_issue_one.issue_id;
                    navigate(
                        isFromActionPlan
                            ? `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}/${issueId}?${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.FROM_ACTION_PLAN}`
                            : `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}/${issueId}`,
                        {
                            state: {
                                from: onAddIssueRedirectTo
                                    ? onAddIssueRedirectTo
                                    : `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.ISSUES.ROOT}`,
                            },
                        },
                    );
                }
                onClose();
            }
        } catch {
            setIsSaving(false);
            onError?.();
            return;
        }
    };

    return (
        <MainModal
            isOpen={open}
            onClose={handleOnClose}
            title={editIssue ? t("rca.issues.add_issue.edit_title") : t("rca.issues.add_issue.title")}
            onOkButtonClick={() => {
                if (formRef.current) {
                    formRef.current.handleSubmit();
                }
            }}
            disableOkButton={isSaving}
            disableEscapeKeyDown
            disableCancelButton={isSaving}
            okButton={
                <>
                    <DoneIcon />
                    {editIssue ? t("rca.issues.add_issue.save_changes") : t("rca.issues.add_issue.create_issue")}
                </>
            }
            cancelButton={t("rca.issues.add_issue.cancel")}
            contentClassName={classes.content}
        >
            <div>
                <Formik
                    innerRef={formRef}
                    initialValues={initFormValues}
                    validationSchema={addRCAIssueFormValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                        return (
                            <Form className={mainClasses.contentForm}>
                                <Grid container spacing={8}>
                                    <Grid item xs={8}>
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-shortDescription-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t("rca.issues.add_issue.short_description")}
                                                </Typography>
                                            </InputLabel>
                                            <TextField
                                                disabled={isSaving || isSubmitting || isLoadingCurrentUser}
                                                aria-labelledby="outlined-shortDescription-label"
                                                fullWidth
                                                id="outlined-shortDescription-input"
                                                className={mainClasses.contentFormTextField}
                                                name="shortDescription"
                                                type="text"
                                                variant="outlined"
                                                value={values.shortDescription}
                                                onChange={handleChange}
                                                error={touched.shortDescription && Boolean(errors.shortDescription)}
                                                helperText={
                                                    touched.shortDescription &&
                                                    errors.shortDescription &&
                                                    t(errors.shortDescription)
                                                }
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-incidentDate-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t("rca.rca_library.rca_detail.incident_date")}
                                                </Typography>
                                            </InputLabel>

                                            <DatePicker
                                                className={`${mainClasses.contentFormDatePicker} ${classes.datePicker}`}
                                                value={dayjs(values.incidentDate)}
                                                minDate={dayjs().subtract(1, "year")}
                                                disableFuture
                                                format={getDateFormatByLanguage()}
                                                onChange={(date) => {
                                                    setFieldValue("incidentDate", date);
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Divider className={classes.divider} />
                                <Box className={mainClasses.contentFormBox}>
                                    <InputLabel id="outlined-description-label">
                                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                            {t("rca.issues.add_issue.description")}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        disabled={isSaving || isSubmitting || isLoadingCurrentUser}
                                        aria-labelledby="outlined-description-label"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        id="outlined-description-input"
                                        className={mainClasses.contentFormMultitextField}
                                        name="description"
                                        type="text"
                                        variant="outlined"
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description && t(errors.description)}
                                    />
                                </Box>
                                {isAdmin && !editIssue && (
                                    <>
                                        <Divider className={classes.divider} />
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-description-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t("rca.issues.add_issue.mill")}
                                                </Typography>
                                            </InputLabel>
                                            <NewMainSelect
                                                fullWidth
                                                name="millId"
                                                options={millOptions || []}
                                                placeholder={t("rca.issues.add_issue.mill_placeholder")}
                                                onChange={(value) => {
                                                    handleChange({
                                                        target: {
                                                            name: "millId",
                                                            value,
                                                        },
                                                    });
                                                }}
                                                value={values.millId && String(values.millId)}
                                            />
                                            {!!errors.millId && !!touched.millId && (
                                                <FormHelperText className={mainClasses.contentFormHelperText}>
                                                    {t(errors.millId)}
                                                </FormHelperText>
                                            )}
                                        </Box>
                                    </>
                                )}
                                <Divider className={classes.divider} />
                                <Box className={mainClasses.contentFormBox}>
                                    <InputLabel id="outlined-description-label">
                                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                            {t("rca.issues.add_issue.key_success_factor")}
                                        </Typography>
                                    </InputLabel>
                                    <NewMainSelect
                                        fullWidth
                                        name="key_success_factors_id"
                                        options={keySuccessFactorOptions || []}
                                        placeholder={t("rca.issues.add_issue.key_success_factor_placeholder")}
                                        onChange={(value) => {
                                            handleChange({
                                                target: {
                                                    name: "key_success_factors_id",
                                                    value: Number(value),
                                                },
                                            });
                                        }}
                                        value={values.key_success_factors_id && String(values.key_success_factors_id)}
                                    />
                                    {!!errors.key_success_factors_id && !!touched.key_success_factors_id && (
                                        <FormHelperText className={mainClasses.contentFormHelperText}>
                                            {t(errors.key_success_factors_id)}
                                        </FormHelperText>
                                    )}
                                </Box>
                                {!editIssue && (
                                    <>
                                        <Divider className={classes.divider} />
                                        <Box className={mainClasses.contentFormBox}>
                                            <InputLabel id="outlined-meetingTypeId-label">
                                                <Typography
                                                    variant="subtitle2"
                                                    className={mainClasses.contentFormLabel}
                                                >
                                                    {t("rca.rca_library.rca_detail.meeting_type")}
                                                </Typography>
                                            </InputLabel>
                                            <RCAMeetingTypesDropdown
                                                fullWidth
                                                multiple={false}
                                                value={values.meetingTypeId}
                                                onChange={(value) => {
                                                    handleChange({
                                                        target: {
                                                            name: "meetingTypeId",
                                                            value,
                                                        },
                                                    });
                                                }}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </MainModal>
    );
};

export default RCACreateEditIssuesModal;
